/* global removeTrapFocus */

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    this.inlineSearch = this.classList.contains('dezin-native-search');
    const input = this.querySelector('.main-search__input');
    this.dismissInline = () => {
      this.classList.remove('dezin-native-search--active');
      this.activeResultIndex = -1;
      input.setAttribute('aria-activedescendant', '');
      input.setAttribute('aria-expanded', 'false');
    };
    this.openInline = () => {
      this.classList.add('dezin-native-search--active');
      input.setAttribute('aria-expanded', String(this.classList.contains('main-search--has-results')));
    };
    input.addEventListener('focus', this.openInline);
    input.addEventListener('input', this.openInline);
    this.outsideSearch = (event) => {
      if (!this.contains(event.target)) this.dismissInline();
    };
    document.addEventListener('pointerdown', this.outsideSearch);
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.dismissInline();
    });

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
        this.dismissInline();
        if (!this.inlineSearch || window.matchMedia('(max-width: 767.98px)').matches) {
          removeTrapFocus(theme.trapOpener);
        }
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }

    // Result navigation
    this.activeResultIndex = -1;
    this.querySelector('.main-search__input').addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.outsideSearch);
  }

  handleKeyDown(evt) {
    const options = [...this.querySelectorAll('.main-search__results [role="option"]')];

    if (!options.length) return;
    if (this.inlineSearch && !this.classList.contains('dezin-native-search--active')) return;

    const updateActiveOption = () => {
      options.forEach((opt) => opt.classList.remove('search-result-active'));
      const active = options[this.activeResultIndex];
      active.classList.add('search-result-active');
      evt.currentTarget.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    };

    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      this.activeResultIndex = Math.min(this.activeResultIndex + 1, options.length - 1);
      updateActiveOption();
    }

    if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeResultIndex = Math.max(this.activeResultIndex - 1, 0);
      updateActiveOption();
    }

    if (evt.key === 'Enter' && this.activeResultIndex >= 0) {
      evt.preventDefault();
      const active = options[this.activeResultIndex];
      const link = active.matches('a') ? active : active.querySelector('a.product-link:not([aria-hidden]), a');
      if (link) link.click();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 8;
    let searchTimeoutID = -1;
    let searchAbortController = null;
    let searchRequestId = 0;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;
      this.activeResultIndex = -1;
      searchInput.setAttribute('aria-activedescendant', '');

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.trim().length && valueToSearch !== this.oldSearchValue) {
        const requestId = ++searchRequestId;
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(async () => {
          const requestController = new AbortController();
          searchAbortController = requestController;
          const resolvedQuery = await window.DezinSearch.query(valueToSearch);
          if (requestController.signal.aborted || requestId !== searchRequestId || searchInput.value !== valueToSearch) return;
          linkURL.searchParams.set('q', resolvedQuery);
          linkURL.searchParams.set('oq', valueToSearch);
          linkURL.searchParams.set('ds', '2');
          linkURL.searchParams.set('options[unavailable_products]', 'show');
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            // use the API
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', resolvedQuery);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[limit]', resultLimit);
            ajaxUrl.searchParams.set('resources[type]', 'product');
            ajaxUrl.searchParams.set('resources[options][unavailable_products]', 'show');
          } else {
            // use the theme template fallback
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxUrl, { method: 'get', signal: requestController.signal })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.text();
            })
            .then((response) => {
              if (requestId !== searchRequestId || searchInput.value !== valueToSearch) return;
              const template = document.createElement('template');
              template.innerHTML = response;
              let resultsList = null;
              let resultsMessage = null;
              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector('.product-grid');
                resultsMessage = template.content.querySelector('[data-results-message]');
              } else {
                resultsList = template.content.querySelector('.section-search-template .product-grid');
                resultsMessage = template.content.querySelector('.section-search-template [data-results-message]');
              }

              if (!resultsList) throw new Error('Search result markup is unavailable');

              // reset previous contents
              searchAbortController = null;
              this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
              resultsBox.innerHTML = '';

              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              resultsProducts.innerHTML = '<div></div>';
              const resultsPages = document.createElement('div');
              resultsPages.className = 'main-search__results__pages';

              resultsProducts.firstElementChild.className = resultsList.className;

              // Convert standard blocks into quick search result format
              resultsList.querySelectorAll('.product-block').forEach((block, index) => {
                block.setAttribute('role', 'option');
                block.id = `SearchResult${index}`;
              });
              resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                if (index < resultLimit) {
                  block.classList.add('main-search-result');
                  block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                  block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                  resultsProducts.firstElementChild.appendChild(block);
                }
              });

              resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                const item = document.createElement('a');
                item.className = 'main-search-result main-search-result--page';
                item.href = block.querySelector('a').href;
                item.innerHTML = '<div class="main-search-result__text"></div>';
                item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                resultsPages.appendChild(item);
              });

              this.classList.remove('main-search--loading');

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');
              if (areProducts || arePages) {
                const status = document.querySelector('#HeaderSearchResultsStatus');
                if (status && resultsMessage) status.textContent = resultsMessage.textContent.split(resolvedQuery).join(valueToSearch);
                // Numerous results
                this.classList.add('main-search--has-results');
                this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block').length > 4);
                if (areProducts) {
                  resultsBox.appendChild(resultsProducts);
                }
                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';
                allLink.href = linkURL;
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                resultsBox.appendChild(allLink);
                searchInput.setAttribute('aria-expanded', String(!this.inlineSearch || this.classList.contains('dezin-native-search--active')));
                this.activeResultIndex = -1;
              } else {
                // No results - show nothing
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                resultsBox.appendChild(emptyMessage);
                searchInput.setAttribute('aria-expanded', false);
              }
            })
            .catch((error) => {
              if (requestController.signal.aborted || error.name === 'AbortError' || requestId !== searchRequestId || searchInput.value !== valueToSearch) return;
              this.classList.remove('main-search--loading');
              this.oldSearchValue = null;
              resultsBox.replaceChildren();
              const fallback = document.createElement('a');
              fallback.className = 'main-search__results-all-link btn btn--secondary';
              fallback.href = linkURL;
              fallback.textContent = theme.strings.generalSearchViewAll;
              resultsBox.appendChild(fallback);
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.trim().length) {
        searchRequestId += 1;
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading', 'main-search--no-results');
        resultsBox.innerHTML = '';
        searchInput.setAttribute('aria-expanded', false);
      }
    };

    searchInput.addEventListener('input', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
    searchInput.addEventListener('focus', handleInputChange.bind(this));
  }
};

window.customElements.define('main-search', MainSearch);



